/**
 * Per-section guidance — the DRY, config-driven content for the persistent
 * ``<SectionGuide>`` help box shown at the top of every workspace section for
 * demo visitors. One entry per section: what it does, which connectors feed it,
 * an optional recommended-path tip, and (for features not exercisable in the
 * sandbox) a "demo-unavailable" note that still sells the real capability.
 *
 * Example prompts are NOT duplicated here — the box reuses
 * ``QUICK_PROMPTS_BY_TAB`` (single source of truth, see ChatEmptyState).
 */
import { msg } from "@lingui/core/macro";

import type { MessageDescriptor } from "@lingui/core";
import type { WorkspaceTab } from "@/features/workspace-router";

export interface SectionGuide {
  /** One-line "what this section is for". */
  readonly description: MessageDescriptor;
  /** Connectors that feed this section (display names — proper nouns). */
  readonly connectors: readonly string[];
  /** Optional recommended-path nudge (e.g. dashboards: connect a DB → generate). */
  readonly tip?: MessageDescriptor;
  /**
   * Set when the feature can't be exercised in the demo sandbox. Renders a
   * banner that still explains what Ando does for real — the UI stays explorable.
   */
  readonly demoNote?: MessageDescriptor;
}

export const SECTION_GUIDES: Partial<Record<WorkspaceTab, SectionGuide>> = {
  conversations: {
    description: msg`Email, PEC e chat di tutti gli account collegati in un'unica casella. Chiedi ad Ando di riassumere, dare priorità o scrivere una risposta — in demo gli invii sono simulati.`,
    connectors: ["Gmail", "Outlook", "Slack", "PEC"],
  },
  calendar: {
    description: msg`Riunioni e impegni di tutti i calendari collegati. Ando può preparare un brief per il prossimo meeting o trovarti uno slot libero.`,
    connectors: ["Google Calendar", "Outlook"],
  },
  documents: {
    description: msg`La conoscenza aziendale — HR, policy, manuali tecnici, troubleshooting — indicizzata e interrogabile in linguaggio naturale, con citazione della fonte.`,
    connectors: ["Google Drive", "OneDrive", "SharePoint"],
  },
  finance: {
    description: msg`Gestionale, banca e fatturazione: scadenziario, cash flow, clienti per fatturato. I dati strutturati (gestionale e banca) si interrogano dal pulsante dedicato «Crea un grafico» nel pannello Dashboard — è da lì che lanci le query SQL su questi dati; la chat non li estrae da sola.`,
    connectors: ["Gestionale", "Fatturazione elettronica", "Open Banking"],
    tip: msg`Per analisi su gestionale e banca usa il pulsante «Crea un grafico» nel pannello Dashboard: scegli la fonte e la query parte sui dati strutturati.`,
  },
  sales: {
    description: msg`Pipeline, lead e opportunità dal CRM. Interroga lo stato delle trattative e prepara follow-up; le scritture sul CRM sono proposte che approvi tu.`,
    connectors: ["Salesforce", "HubSpot"],
  },
  work: {
    description: msg`Task, attività e azioni proposte da Ando. Ogni azione è una proposta che vedi, modifichi e approvi prima dell'esecuzione (in demo è simulata).`,
    connectors: [],
  },
  dashboards: {
    description: msg`Dashboard e grafici sui tuoi dati strutturati (banca, gestionale, database). Le query sui dati strutturati si lanciano dal pulsante dedicato «Crea un grafico»: scegli la fonte e la query SQL parte da lì, non dalla chat.`,
    connectors: ["Database enterprise", "Gestionale"],
    tip: msg`Percorso consigliato: collega una fonte dati, poi premi «Crea un grafico», scegli la fonte e descrivi cosa vuoi vedere (es. «fatturato per mese»). Fissa il grafico sulla dashboard.`,
  },
  automations: {
    description: msg`Automazioni che Ando esegue per te su base ricorrente.`,
    connectors: [],
    tip: msg`In questa demo è già attiva un'automazione "Riassunto mail giornaliero": la trovi qui nell'elenco, con stato ed esecuzioni.`,
  },
  canvases: {
    description: msg`Le Canvas sono spazi di conoscenza verticali — un cervello dedicato per cliente, progetto o tema, che combina documenti, dati e ragionamento.`,
    connectors: [],
    tip: msg`Apri una delle Canvas di esempio per vedere come si combinano fonti e analisi in un unico spazio.`,
  },
  technical: {
    description: msg`Disegni 2D/3D, distinte base (BOM) e revisioni tecniche. I dati strutturati dei file CAD si interrogano dal pulsante dedicato del pannello, non dalla chat.`,
    connectors: ["CAD"],
    demoNote: msg`Non utilizzabile nella demo. Con Ando puoi gestire, interrogare e generare file 2D/3D nei formati standard (DXF, DWG, STEP, IGES, STL). Puoi comunque esplorare l'interfaccia.`,
  },
  flows: {
    description: msg`Flussi visuali per orchestrare processi ricorrenti su più sistemi.`,
    connectors: [],
    demoNote: msg`Disponibile nella versione completa di Ando. Qui puoi esplorare l'interfaccia per capire come si costruisce un flusso.`,
  },
};
